import { useEffect, useState } from 'react';
import { api, fmt, fmtDate, type AdminUser } from '../api';
import { useAction, useAsync } from '../hooks';
import { navigate, usePath } from '../router';

/** Ученики: поиск, карточка, ручная корректировка баланса (ТЗ §7.5). */
export function UsersPage() {
  const path = usePath();
  const [, setLocationKey] = useState(
    () => location.pathname + location.search,
  );

  useEffect(() => {
    const sync = () => setLocationKey(location.pathname + location.search);

    window.addEventListener('navigate', sync);
    window.addEventListener('popstate', sync);

    return () => {
      window.removeEventListener('navigate', sync);
      window.removeEventListener('popstate', sync);
    };
  }, []);

  const selected = new URLSearchParams(location.search).get('id');

  if (path === '/users' && selected) {
    return <UserCard userId={selected} />;
  }

  return <UsersList />;
}

function UsersList() {
  const PAGE_SIZE = 25;

  const [query, setQuery] = useState('');
  const [applied, setApplied] = useState('');
  const [offset, setOffset] = useState(0);

  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const changeSort = (key: string) => {
    setOffset(0);

    if (sortBy === key) {
      setSortDir((current) => (current === 'desc' ? 'asc' : 'desc'));
      return;
    }

    setSortBy(key);
    setSortDir('desc');
  };

  const sortLabel = (key: string) =>
    sortBy === key ? (sortDir === 'desc' ? '▼' : '▲') : '↕';

  const { data, loading, error } = useAsync(
    () => api.users(applied, PAGE_SIZE, offset, sortBy, sortDir),
    [applied, offset, sortBy, sortDir],
  );

  const total = data?.total ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <>
      <h1>Ученики</h1>
      <p className="subtitle">
        Все ученики, которые участвуют в реферальной программе — в том числе те,
        кто ещё ни разу не входил в новый бонусный кабинет.
      </p>

      <form
        className="row"
        style={{ marginBottom: 16 }}
        onSubmit={(e) => {
          e.preventDefault();
          setOffset(0);
          setApplied(query.trim());
        }}
      >
        <input
          placeholder="ID ученика или реферальный код"
          style={{ maxWidth: 420 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button type="submit">Найти</button>

        {applied && (
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setQuery('');
              setApplied('');
              setOffset(0);
            }}
          >
            Сбросить
          </button>
        )}
      </form>

      {error && <div className="notice err">{error}</div>}

      {!loading && !error && (
        <p className="subtitle">
          Всего участников: <strong>{fmt(total)}</strong>
          {total > 0 && <> · показаны {fmt(from)}–{fmt(to)}</>}
        </p>
      )}

      <div className="card users-table-card">
        <table className="users-table">
          <thead>
            <tr>
              <th>
                <button
                  type="button"
                  className="sort-button"
                  onClick={() => changeSort('displayName')}
                >
                  Ученик <span>{sortLabel('displayName')}</span>
                </button>
              </th>

              <th>
                <button
                  type="button"
                  className="sort-button"
                  onClick={() => changeSort('vkId')}
                >
                  VK <span>{sortLabel('vkId')}</span>
                </button>
              </th>

              <th>Кабинет</th>

              <th className="num">
                <button
                  type="button"
                  className="sort-button num"
                  onClick={() => changeSort('totalOrders')}
                >
                  Заказов <span>{sortLabel('totalOrders')}</span>
                </button>
              </th>

              <th className="num">
                <button
                  type="button"
                  className="sort-button num"
                  onClick={() => changeSort('paidOrders')}
                >
                  Оплачено <span>{sortLabel('paidOrders')}</span>
                </button>
              </th>

              <th className="num">
                <button
                  type="button"
                  className="sort-button num"
                  onClick={() => changeSort('confirmedReferees')}
                >
                  Подтверждено <span>{sortLabel('confirmedReferees')}</span>
                </button>
              </th>

              <th className="num">
                <button
                  type="button"
                  className="sort-button num"
                  onClick={() => changeSort('hold')}
                >
                  В холде <span>{sortLabel('hold')}</span>
                </button>
              </th>

              <th className="num">
                <button
                  type="button"
                  className="sort-button num"
                  onClick={() => changeSort('available')}
                >
                  Текущий баланс <span>{sortLabel('available')}</span>
                </button>
              </th>

              <th className="num">
                <button
                  type="button"
                  className="sort-button num"
                  onClick={() => changeSort('legacyBalance')}
                >
                  Legacy-баланс <span>{sortLabel('legacyBalance')}</span>
                </button>
              </th>

              <th>
                <button
                  type="button"
                  className="sort-button"
                  onClick={() => changeSort('lastLogin')}
                >
                  Последний вход <span>{sortLabel('lastLogin')}</span>
                </button>
              </th>

              <th />
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={11}>Загружаем…</td>
              </tr>
            )}

            {(data?.items ?? []).map((u: AdminUser) => (
              <tr key={u.external_user_id}>
                <td>
                  <strong>{u.display_name ?? `ID ${u.external_user_id}`}</strong>
                  <div>
                    <span className="badge off">{u.external_user_id}</span>
                    {u.is_blocked && (
                      <span className="badge err">заблокирован</span>
                    )}
                  </div>
                </td>

                <td>{u.vk_id ?? '—'}</td>

                <td>
                  {u.has_account ? (
                    <span className="badge">Входил</span>
                  ) : (
                    <span className="badge off">Не входил</span>
                  )}
                </td>

                <td className="num">{fmt(u.total_orders ?? 0)}</td>
                <td className="num">{fmt(u.paid_orders ?? 0)}</td>
                <td className="num">{fmt(u.confirmed_referees ?? 0)}</td>

                <td className="num">
                  {fmt(u.balance?.hold ?? 0)}
                </td>

                <td className="num">
                  {fmt(u.balance?.available ?? 0)}
                </td>

                <td className="num">
                  {u.legacy_balance === null || u.legacy_balance === undefined ? (
                    '—'
                  ) : u.legacy_balance > 15000 ? (
                    <>
                      <strong>{fmt(u.legacy_balance)}</strong>
                      <div>
                        <span className="badge err">проверить</span>
                      </div>
                    </>
                  ) : (
                    fmt(u.legacy_balance)
                  )}
                </td>

                <td>
                  {u.has_account ? fmtDate(u.last_login_at) : '—'}
                </td>

                <td>
                  {u.has_account && u.id ? (
                    <button
                      className="secondary small"
                      onClick={() => navigate(`/users?id=${u.id}`)}
                    >
                      Открыть
                    </button>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))}

            {!loading && (data?.items.length ?? 0) === 0 && (
              <tr>
                <td colSpan={11} className="muted">
                  Никого не нашли
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!loading && total > PAGE_SIZE && (
        <div
          className="row"
          style={{
            marginTop: 16,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            className="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            ← Назад
          </button>

          <span className="subtitle" style={{ margin: 0 }}>
            {fmt(from)}–{fmt(to)} из {fmt(total)}
          </span>

          <button
            className="secondary"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Далее →
          </button>
        </div>
      )}
    </>
  );
}

function UserCard({ userId }: { userId: string }) {
  const { data, loading, error, reload } = useAsync(() => api.user(userId), [userId]);
  const accruals = useAsync(() => api.userAccruals(userId), [userId]);
  const transactions = useAsync(() => api.userTransactions(userId), [userId]);

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [allowNegative, setAllowNegative] = useState(false);

  const adjust = useAction(async () => {
    await api.adjust(userId, Number(amount), reason, allowNegative);
    setAmount('');
    setReason('');
    reload();
    transactions.reload();
  });
  const block = useAction(async (blocked: boolean) => {
    await api.block(userId, blocked, blocked ? reason || 'Заблокирован администратором' : null);
    reload();
  });

  if (loading || !data) return <p className="subtitle">{error ?? 'Загружаем…'}</p>;

  const amountNum = Number(amount);
  const canAdjust = Number.isInteger(amountNum) && amountNum !== 0 && reason.trim().length >= 5;

  return (
    <>
      <button className="secondary small" onClick={() => navigate('/users')}>
        ← К списку
      </button>
      <h1 style={{ marginTop: 12 }}>{data.user.display_name ?? data.user.external_user_id}</h1>
      <p className="subtitle">
        id в основной платформе: {data.user.external_user_id} · VK: {data.user.vk_id ?? '—'}
      </p>

      <div className="grid cols-4">
        <div className="stat">
          <div className="v">{fmt(data.balance.available)}</div>
          <div className="l">Доступно</div>
        </div>
        <div className="stat">
          <div className="v">{fmt(data.balance.hold)}</div>
          <div className="l">В холде</div>
        </div>
        <div className="stat">
          <div className="v">{fmt(data.balance.pending)}</div>
          <div className="l">На подтверждении</div>
        </div>
        <div className="stat">
          <div className="v">{String((data.summary as { invitedCount?: number }).invitedCount ?? 0)}</div>
          <div className="l">Приглашённых в счётчике</div>
        </div>
      </div>

      {data.balance.inDebt && (
        <div className="notice warn">
          Баланс отрицательный: после возврата заказа списание ушло в минус. Долг погасится будущими начислениями.
        </div>
      )}

      <h2>Ручная корректировка баланса</h2>
      <div className="card">
        <p className="subtitle" style={{ marginTop: 0 }}>
          Причина обязательна и попадает в журнал вместе с автором и точным временем.
        </p>
        {adjust.error && <div className="notice err">{adjust.error}</div>}
        {adjust.ok && <div className="notice ok">Баланс скорректирован</div>}
        <div className="grid cols-3">
          <div className="field">
            <label>Сколько бонусов (можно со знаком минус)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Причина</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Например: компенсация за сбой 14.08" />
          </div>
        </div>
        <label className="row" style={{ gap: 8, marginBottom: 12 }}>
          <input type="checkbox" checked={allowNegative} onChange={(e) => setAllowNegative(e.target.checked)} />
          Разрешить увести баланс в минус
        </label>
        <div className="row">
          <button disabled={!canAdjust || adjust.busy} onClick={() => void adjust.run()}>
            {adjust.busy ? 'Применяем…' : 'Применить'}
          </button>
          <button
            className={data.user.is_blocked ? 'secondary' : 'danger'}
            disabled={block.busy}
            onClick={() => void block.run(!data.user.is_blocked)}
          >
            {data.user.is_blocked ? 'Разблокировать' : 'Заблокировать участие'}
          </button>
        </div>
      </div>

      <h2>Начисления</h2>
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Заказ</th>
              <th>Тип</th>
              <th>Состояние</th>
              <th className="num">Ступень</th>
              <th className="num">Бонусов</th>
              <th>Выход из холда</th>
            </tr>
          </thead>
          <tbody>
            {(accruals.data?.items ?? []).map((a) => {
              const r = a as Record<string, string | number | null>;
              return (
                <tr key={String(r.id)}>
                  <td>{String(r.externalOrderId)}</td>
                  <td>{String(r.orderType)}</td>
                  <td>
                    <span className="badge">{String(r.state)}</span>
                    {r.rejectReason && <div className="badge off">{String(r.rejectReason)}</div>}
                  </td>
                  <td className="num">{r.ordinal ?? '—'}</td>
                  <td className="num">{fmt(Number(r.amount))}</td>
                  <td>{fmtDate(r.releaseAt as string | null)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h2>Движение бонусов</h2>
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Операция</th>
              <th className="num">Сумма</th>
              <th className="num">Доступный баланс</th>
              <th>Причина</th>
              <th>Когда</th>
            </tr>
          </thead>
          <tbody>
            {(transactions.data?.items ?? []).map((t) => {
              const r = t as Record<string, string | number | null>;
              return (
                <tr key={String(r.id)}>
                  <td>{String(r.type)}</td>
                  <td className="num">{fmt(Number(r.amount))}</td>
                  <td className="num">{fmt(Number(r.availableDelta))}</td>
                  <td>{(r.reason as string) ?? '—'}</td>
                  <td>{fmtDate(r.createdAt as string)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
